import { useState } from "react";
import * as XLSX from "xlsx";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Upload, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

/**
 * Commit Mailed File
 *
 * Workflow:
 *   1. Drop an .xlsx export that was actually mailed.
 *   2. Type campaign name + mailed date.
 *   3. Match rows by `Seller ID` (case-insensitive header), insert mailing_history
 *      with the correct per-record mailing_round, bump times_mailed and
 *      last_mailed_date on mailing_records.
 */
export function CommitMailedFile() {
  const [file, setFile] = useState<File | null>(null);
  const [sellerIds, setSellerIds] = useState<string[]>([]);
  const [campaignName, setCampaignName] = useState("");
  const [mailDate, setMailDate] = useState(new Date().toISOString().split("T")[0]);
  const [parsing, setParsing] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [lastResult, setLastResult] = useState<{
    matched: number;
    missing: number;
    missingIds: string[];
  } | null>(null);

  const handleFile = async (f: File) => {
    setFile(f);
    setSellerIds([]);
    setLastResult(null);
    setParsing(true);
    try {
      const buf = await f.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array" });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json<Record<string, any>>(ws, { defval: "", raw: true });

      const headers = Object.keys(rows[0] || {});
      console.log("[CommitMailedFile] headers detected:", headers);

      const norm = (k: string) => k.toLowerCase().replace(/[\s_\-\.]/g, "");
      // Accept common variants: "Seller ID", "SellerID", "Seller_ID", "ID", "Record ID"
      const candidates = ["sellerid", "id", "recordid", "mailingid", "mailingrecordid"];
      let idKey = headers.find((k) => candidates.includes(norm(k)));

      // Fallback: any header containing "seller" and "id"
      if (!idKey) {
        idKey = headers.find((k) => {
          const n = norm(k);
          return n.includes("seller") && n.includes("id");
        });
      }

      if (!idKey) {
        toast.error(
          `No Seller ID column found. Headers seen: ${headers.join(", ") || "(none)"}`
        );
        setParsing(false);
        return;
      }

      const ids = Array.from(
        new Set(
          rows
            .map((r) => {
              const v = r[idKey!];
              if (v === null || v === undefined) return "";
              // Strip trailing ".0" from numeric IDs read as floats
              return String(v).trim().replace(/\.0+$/, "");
            })
            .filter((v) => v.length > 0)
        )
      );
      setSellerIds(ids);
      if (ids.length === 0) {
        toast.error(`Column "${idKey}" found but contained no IDs`);
      } else {
        toast.success(`Found ${ids.length} unique Seller IDs (column "${idKey}") in ${rows.length} rows`);
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to parse file");
    } finally {
      setParsing(false);
    }
  };

  const commit = async () => {
    console.log("[CommitMailedFile] commit clicked", { sellerIds: sellerIds.length, campaignName, mailDate });
    if (!sellerIds.length) return toast.error("Upload a file first (no Seller IDs parsed)");
    if (!campaignName.trim()) return toast.error("Campaign name is required");
    if (!mailDate) return toast.error("Mailed date is required");

    setCommitting(true);
    setLastResult(null);
    try {
      // Fetch matching records in chunks (PostgREST .in() ceiling = 200)
      const chunkSize = 200;
      const found: { id: string; seller_id: string; times_mailed: number }[] = [];
      for (let i = 0; i < sellerIds.length; i += chunkSize) {
        const slice = sellerIds.slice(i, i + chunkSize);
        const { data, error } = await supabase
          .from("mailing_records")
          .select("id, seller_id, times_mailed")
          .in("seller_id", slice);
        if (error) throw error;
        if (data) found.push(...(data as any));
      }

      const foundIds = new Set(found.map((r) => r.seller_id));
      const missingIds = sellerIds.filter((s) => !foundIds.has(s));

      if (found.length === 0) {
        toast.error("No mailing records matched the Seller IDs in this file");
        setLastResult({ matched: 0, missing: missingIds.length, missingIds });
        setCommitting(false);
        return;
      }

      // Insert history + bump record counters in batches of 500
      const batchSize = 500;
      for (let i = 0; i < found.length; i += batchSize) {
        const batch = found.slice(i, i + batchSize);

        const historyEntries = batch.map((rec) => ({
          mailing_record_id: rec.id,
          campaign_name: campaignName.trim(),
          mailed_date: mailDate,
          mailing_round: (rec.times_mailed || 0) + 1,
        }));

        const { error: histErr } = await supabase
          .from("mailing_history")
          .insert(historyEntries);
        if (histErr) throw histErr;

        for (const rec of batch) {
          const { error: updErr } = await supabase
            .from("mailing_records")
            .update({
              times_mailed: (rec.times_mailed || 0) + 1,
              last_mailed_date: mailDate,
            })
            .eq("id", rec.id);
          if (updErr) throw updErr;
        }
      }

      toast.success(
        `Committed ${found.length} records to "${campaignName.trim()}" (${missingIds.length} unmatched)`
      );
      setLastResult({ matched: found.length, missing: missingIds.length, missingIds });
      setFile(null);
      setSellerIds([]);
      setCampaignName("");
    } catch (err: any) {
      console.error(err);
      toast.error(`Commit failed: ${err?.message ?? "unknown error"}`);
    } finally {
      setCommitting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Upload className="h-5 w-5" /> Commit Mailed File
        </CardTitle>
        <CardDescription>
          Drop the exact spreadsheet you mailed. We match by <b>Seller ID</b>, log the
          campaign in mailing history, and bump times_mailed + last_mailed_date.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <Label>Mailed file (.xlsx)</Label>
          <Input
            type="file"
            accept=".xlsx,.xls"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handleFile(f);
            }}
            disabled={parsing || committing}
          />
          {file && (
            <p className="text-sm text-muted-foreground mt-1">
              {file.name} — {sellerIds.length} unique Seller IDs
            </p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Campaign name</Label>
            <Input
              value={campaignName}
              onChange={(e) => setCampaignName(e.target.value)}
              placeholder="e.g. Investors"
              disabled={committing}
            />
          </div>
          <div>
            <Label>Mailed date</Label>
            <Input
              type="date"
              value={mailDate}
              onChange={(e) => setMailDate(e.target.value)}
              disabled={committing}
            />
          </div>
        </div>

        <Button
          onClick={commit}
          disabled={committing || parsing}
          className="w-full"
        >
          {committing ? "Committing..." : `Commit ${sellerIds.length || 0} records`}
        </Button>
        {!sellerIds.length && file && !parsing && (
          <p className="text-sm text-destructive">
            No Seller IDs parsed from the file. Check that the first column is "Seller ID".
          </p>
        )}

        {lastResult && (
          <div className="rounded-md border p-3 text-sm space-y-1">
            <div className="flex items-center gap-2 font-medium">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              Matched: {lastResult.matched} • Unmatched: {lastResult.missing}
            </div>
            {lastResult.missing > 0 && (
              <details className="text-muted-foreground">
                <summary className="cursor-pointer">Show unmatched Seller IDs</summary>
                <pre className="mt-2 max-h-48 overflow-auto text-xs">
                  {lastResult.missingIds.join("\n")}
                </pre>
              </details>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
